import _ from 'lodash';
import i18n from 'i18next';
import moment from 'moment';
import forge from 'node-forge';
import { showAlert } from './notifications';
import { clearUserEnvironment, getUserEnvironment } from './caching';
import { ACCOUNT_REGIONS, MASKED_NUMBER_PHONE_LENGTH, MAX_COIN_ADDRESS_LENGTH } from './constants';

let keyboardHeight = 0;
const screenHeight = window.innerHeight;
let checkIfNoKeydown;

export const isClCard = (currencyCode) => {
  if (currencyCode.length > 3 && currencyCode.substring(0, 2) === 'cl') {
    return true;
  }
  return false;
};

export const keysToUpperCase = (obj) => {
  const objUpperCase = _.mapKeys(obj, (value, key) => key.toUpperCase());
  return objUpperCase;
};

export const filterUsableWallets = (wallets) => wallets.filter((wallet) => !isClCard(wallet.coin));

export const toLowerCaseArray = (array) => array.map((element) => element.toLowerCase());

const setViewPortHeight = (hasModal, hasCard) => {
  document.documentElement.style.setProperty('overflow', 'auto');
  if (hasCard.length > 0) {
    hasCard[0].style.paddingBottom = `${keyboardHeight}px`;
    hasCard[0].style.display = 'none';
  }
  if (hasModal.length > 0) {
    hasModal[0].style.paddingBottom = `${keyboardHeight}px`;
  }
  document.documentElement.style.setProperty('overflow', 'auto');
  const metaViewport = document.querySelector('meta[name=viewport]');
  metaViewport.setAttribute(
    'content',
    `height=${screenHeight}px, width=device-width, initial-scale=1.0`
  );
};

const resetToNormal = (hasModal, hasCard) => {
  const metaViewport = document.querySelector('meta[name=viewport]');
  metaViewport.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
  );
  document.documentElement.style.setProperty('overflow', '');
  if (hasModal.length > 0) {
    hasModal[0].style.paddingBottom = '';
  }

  if (hasCard.length > 0) {
    hasCard[0].style.paddingBottom = '';
    hasCard[0].style.display = '';
  }
};

const handleKeyBoardMobile = (e) => {
  clearInterval(checkIfNoKeydown);
  checkIfNoKeydown = setInterval(() => {
    const hasModal = document.body.querySelectorAll('.modal.d-block .modal-dialog');
    const hasCard = document.body.querySelectorAll('.card-footer');
    if (
      e.target &&
      e.target.tagName === 'INPUT' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('type') !== 'radio'
    ) {
      if (!keyboardHeight) {
        setTimeout(() => {
          if (screenHeight !== window.innerHeight) {
            keyboardHeight = screenHeight - window.innerHeight;
            setViewPortHeight(hasModal, hasCard);
          }
        }, 900);
      } else {
        setViewPortHeight(hasModal, hasCard);
      }
    } else {
      resetToNormal(hasModal, hasCard);
      clearInterval(checkIfNoKeydown);
    }

    if (screenHeight - window.innerHeight === 0) {
      resetToNormal(hasModal, hasCard);
      clearInterval(checkIfNoKeydown);
    }
  }, 1000);
};

export const handleWindowViewPort = () => {
  document.body.addEventListener('click', (e) => {
    handleKeyBoardMobile(e);
  });
  document.body.addEventListener('blur', (e) => {
    handleKeyBoardMobile(e);
  });
  document.body.addEventListener('focus', (e) => {
    handleKeyBoardMobile(e);
  });
  document.body.addEventListener('keydown', (e) => {
    handleKeyBoardMobile(e);
  });
};

export const isArrayEquals = (data1, data2) => _(data1).xorWith(data2, _.isEqual).isEmpty();

export const alphaSort = (
  obj1,
  obj2,
  key,
  ascending = false,
  parsingToNumber = false,
  isDate = false
) => {
  const firstItem = parsingToNumber ? parseFloat(obj1[key] === 'NaN' ? 0 : obj1[key]) : obj1[key];
  const secondItem = parsingToNumber ? parseFloat(obj2[key] === 'NaN' ? 0 : obj2[key]) : obj2[key];

  if (isDate) {
    return ascending
      ? moment.utc(obj1[key]).diff(moment.utc(obj2[key]))
      : moment.utc(obj2[key]).diff(moment.utc(obj1[key]));
  }

  if (ascending) {
    if (firstItem < secondItem) {
      return 1;
    }
    if (firstItem > secondItem) {
      return -1;
    }
    return 0;
  }
  // descending mode.
  if (firstItem > secondItem) {
    return 1;
  }
  if (firstItem < secondItem) {
    return -1;
  }
  return 0;
};

const prepareAssets = (require, folder) =>
  require.keys().reduce((acc, next) => {
    // eslint-disable-next-line import/no-dynamic-require
    acc[next.replace('./', `./${folder}/`)] = require(next);
    return acc;
  }, {});

export const customerImages = prepareAssets(
  require.context('assets/images', true, /\.(png|svg|jpe?g|gif)$/),
  'assets/images'
);
export const customerIcons = prepareAssets(
  require.context('assets/icons', true, /\.(png|svg|jpe?g|gif)$/),
  'assets/icons'
);

export const images = prepareAssets(
  require.context('../assets/images', true, /\.(png|svg|jpe?g|gif)$/),
  'assets/images'
);

export const publicImages = prepareAssets(
  require.context('../../public/images', true, /\.(png|svg|jpe?g|gif)$/),
  'assets/images'
);

export const icons = prepareAssets(
  require.context('../assets/icons', true, /\.(png|svg|jpe?g|gif)$/),
  'assets/icons'
);

let assetsCache = null;

export const dynamicAssets = (file) => {
  if (file && assetsCache && assetsCache[file]) {
    return assetsCache[file];
  }

  if (!assetsCache) {
    const allImages = { ...images, ...customerImages, ...publicImages };
    const allIcons = { ...icons, ...customerIcons };
    assetsCache = { ...allImages, ...allIcons };
  }

  return file ? assetsCache[file] || undefined : assetsCache;
};

export const copyToClipboard = async (string, withoutSpaces = false) => {
  try {
    await navigator.clipboard.writeText(withoutSpaces ? string?.replaceAll(/\s/g, '') : string);
    showAlert({ message: i18n.t('copied_to_clipboard_msg'), type: 'success' });
  } catch (error) {
    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          data: withoutSpaces ? string?.replaceAll(/\s/g, '') : string,
          method: 'copy',
        })
      );
      showAlert({ message: i18n.t('copied_to_clipboard_msg'), type: 'success' });
    } catch (e) {
      showAlert({ message: i18n.t('copied_to_clipboard_failure_msg'), type: 'danger' });
    }
  }
};
export const removeWhitespaces = (string) => string?.replace(/ /g, '');

export const identifyZendeskUser = (zendeskJWT) => {
  window.zE('messenger', 'loginUser', (callback) => {
    callback(zendeskJWT);
  });
  window?.zE('messenger', 'show');
};

export const parseJwt = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
};

export const setupSupportWidget = (zendeskJWT, debugZendeskJWT = false) => {
  // eslint-disable-next-line func-names
  (function () {
    const d = document;
    const s = d.createElement('script');
    s.id = 'ze-snippet';
    s.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.REACT_APP_ZENDESK_SNIPPET_ID}`;
    s.async = 1;
    d.getElementsByTagName('head')[0].appendChild(s);
  })();
  if (debugZendeskJWT) {
    // eslint-disable-next-line no-console
    console.log('#Zendesk JWT Decoded: ', parseJwt(zendeskJWT));
  }
  setTimeout(() => {
    window?.$zopim?.livechat?.button?.setOffsetVerticalMobile(75);
    identifyZendeskUser(zendeskJWT);
  }, 2000);
};

export const hideSupportWidget = async () => {
  await window.zE('messenger', 'logoutUser');
  await window.zE('messenger', 'close');
  await window?.zE('messenger', 'hide');
  // Or hide the entire web widget
  await window?.zE('webWidget', 'hide');
};

export const hideSupportWidgetWithoutLogout = async () => {
  if (window?.zE) {
    await window?.zE('messenger', 'close');
    await window?.zE('messenger', 'hide');
    // Or hide the entire web widget
    await window?.zE('webWidget', 'hide');
  }
};

export const generateExternalTransactionURL = (coin, transactionId) => {
  const coinLowerCase = coin.toLowerCase();
  switch (coinLowerCase) {
    case 'btc':
      return `https://www.blockchain.com/btc/tx/${transactionId}`;
    case 'xrp':
      return `https://www.xrpscan.com/tx/${transactionId}`;
    case 'xtz':
      return `https://tzstats.com/${transactionId}`;
    case 'eth':
      // ETH & all ERC20 tokens
      return `https://etherscan.io/tx/${transactionId}`;
    case 'ltc':
      return `https://blockchair.com/litecoin/transaction/${transactionId}`;
    case 'sol':
      return `https://solscan.io/tx/${transactionId}`;
    default:
      return '#';
  }
};

export const extractStyleAsNumber = (element, css) =>
  parseFloat(window.getComputedStyle(element)[css]);

const regPattern = /^([RA]{2})?\d+$/i;
export const errorCodeIsRiskAssessmentWarning = (errorcode) => regPattern.test(errorcode);

export const getUrlEnvironment = () => {
  if (window.location.href.includes(process.env.REACT_APP_FE_BASE_URL)) {
    return ACCOUNT_REGIONS.UK_OR_EU;
  }
  if (window.location.href.includes(process.env.REACT_APP_US_FE_BASE_URL)) {
    return ACCOUNT_REGIONS.US;
  }

  return ACCOUNT_REGIONS.UK_OR_EU;
};

export const gotoUrlEnvironment = (env, url = '') => {
  if (env === ACCOUNT_REGIONS.UK_OR_EU) {
    window.location.href = `${process.env.REACT_APP_FE_BASE_URL}${
      // eslint-disable-next-line no-restricted-globals
      location.hostname.indexOf('baanx.co.uk') > 0 ? '/iframeV2/view' : ''
    }/${url}`;
  } else if (env === ACCOUNT_REGIONS.US) {
    window.location.href = `${process.env.REACT_APP_US_FE_BASE_URL}${
      // eslint-disable-next-line no-restricted-globals
      location.hostname.indexOf('baanx.co.uk') > 0 ? '/iframeV2/view' : ''
    }/${url}`;
  }
};

export const handleUserEnvironmentUrl = (url = '', resetMainEnv = false) => {
  const userEnv = getUserEnvironment();
  const urlEnv = getUrlEnvironment();
  const defaultEnv = ACCOUNT_REGIONS.UK_OR_EU;

  if (resetMainEnv) {
    gotoUrlEnvironment(defaultEnv, `${url}?reset-env=true`);
  }

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  const shouldResetEnv = params['reset-env'];

  if (userEnv && urlEnv === ACCOUNT_REGIONS.US) {
    clearUserEnvironment();
    return;
  }

  if (shouldResetEnv) {
    clearUserEnvironment();
    window.location.href = window.location.href.replace('?reset-env=true', '');
    return;
  }

  if (userEnv && userEnv !== urlEnv && userEnv === ACCOUNT_REGIONS.US) {
    gotoUrlEnvironment(ACCOUNT_REGIONS.US, url);
  }
};

export const getReadableFileSizeString = (fileSizeInBytes) => {
  let i = -1;
  const byteUnits = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let result = fileSizeInBytes;
  do {
    result /= 1024;
    // eslint-disable-next-line no-plusplus
    i++;
  } while (result > 1024);

  return {
    display: `${Math.round(Math.max(result, 0.1)).toFixed(2)} ${byteUnits[i]}`,
    number: Math.round(Math.max(result, 0.1)),
    unit: byteUnits[i],
  };
};

export const htmlDecode = (content) => {
  const e = document.createElement('div');
  e.innerHTML = content;
  return e.childNodes.length === 0 ? '' : e.childNodes[0]?.outerHTML;
};

export const starPhoneNumber = (value) => {
  const completeNumber = value;

  const firstPart = completeNumber.slice(0, 3);
  const lastPart = completeNumber.slice(completeNumber.length - 3, completeNumber.length);
  const stars = Array(completeNumber.length - 6).fill('*');

  return `${firstPart}${stars.join('')}${lastPart}`;
};

export const maskCoinAddress = (value) => {
  const completeNumber = value;

  if (value.length > MAX_COIN_ADDRESS_LENGTH) {
    const firstPart = completeNumber.slice(0, 15);
    const lastPart = completeNumber.slice(completeNumber.length - 15, completeNumber.length);
    const dots = Array(3).fill('.');

    return `${firstPart}${dots.join('')}${lastPart}`;
  }
  return value;
};

export const getMaskedPhoneNumber = (phoneNumber = '', countryCode = '') => {
  let maskedPhoneNumber = '';
  if (phoneNumber?.indexOf('*') !== -1) {
    maskedPhoneNumber = phoneNumber;
  } else {
    const newCountryCode = countryCode?.replace('+', '');
    let newPhoneNumber = phoneNumber.replace('+', '');

    const cutPhoneNumberFrom = newCountryCode.length + MASKED_NUMBER_PHONE_LENGTH;
    newPhoneNumber = removeWhitespaces(newPhoneNumber);
    maskedPhoneNumber = `+${newCountryCode}${'*'.repeat(
      MASKED_NUMBER_PHONE_LENGTH
    )}${phoneNumber.substr(cutPhoneNumberFrom, newPhoneNumber.length - 1)}`;
  }
  return maskedPhoneNumber;
};

export const isObject = (item) =>
  typeof item === 'object' && Object.prototype.toString.call(item) === '[object Object]';

export const encryptWithPublicKey = async (publicKeyPEM, data) => {
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPEM);

  const dataInBytes = forge.util.createBuffer(data, 'utf8').bytes();

  const encryptedData = publicKey.encrypt(dataInBytes, 'RSA-OAEP', {
    md: forge.md.sha256.create(), // SHA-256 for hashing
    mgf1: forge.mgf1.create(forge.md.sha256.create()), // MGF1 with SHA-256
  });
  return forge.util.encode64(encryptedData);
};

export const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string') {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getWordCount = (sentence) => {
  const words = sentence.match(/\b\w+\b/g);
  return words ? words.length : 0;
};
